import React from 'react';
import styled, { css } from 'styled-components';

import { Button } from '../button';
import { useModalContext } from './context';
import { ModalFooterProps } from './types';

const StyledFooter = styled.div<{ $reverse: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.modal.contentBackgroundColor};
  border-top: 1px solid ${({ theme }) => theme.modal.footer.borderColor};
  min-height: 30px;
  padding: 4px 5px;

  button:only-child {
    margin-left: auto;
  }

  ${({ $reverse }) =>
    $reverse &&
    css`
      flex-direction: row-reverse;
    `}
`;

const FooterInfoContainer = styled.div`
  margin-right: 10px;
  font-size: 11px;
  line-height: 13px;
  margin-left: 0.5rem;
  flex: 1;
`;

const destructiveStyle: React.CSSProperties = {
  backgroundColor: '#dc3545',
  borderColor: '#dc3545',
  color: '#fff',
};

export const ModalFooter: React.FC<ModalFooterProps> = ({
  confirmText,
  cancelText,
  onConfirm: onConfirmProp,
  confirmDisabled = false,
  destructive = false,
  reverseActions = false,
  footerInfo,
  cancelTestId,
  confirmTestId,
  children,
}) => {
  const { dismiss, onConfirm: onConfirmCtx, loading } = useModalContext();
  const handleConfirm = onConfirmProp ?? onConfirmCtx;

  if (children) {
    return <StyledFooter $reverse={false}>{children}</StyledFooter>;
  }

  const hasConfirm = !!(handleConfirm || confirmText);
  const hasCancel = !!cancelText;

  if (!hasConfirm && !hasCancel && !footerInfo) {
    return null;
  }

  return (
    <StyledFooter $reverse={reverseActions}>
      {hasCancel && (
        <Button
          data-testid={cancelTestId ?? 'modal-cancel-button'}
          buttonSize="small"
          buttonType={hasConfirm ? 'secondary' : 'primary'}
          onClick={loading ? undefined : dismiss}
          disabled={loading}
        >
          {cancelText}
        </Button>
      )}
      {footerInfo && <FooterInfoContainer>{footerInfo}</FooterInfoContainer>}
      {hasConfirm && (
        <Button
          data-testid={confirmTestId ?? 'modal-confirm-button'}
          buttonSize="small"
          buttonType="primary"
          onClick={handleConfirm}
          disabled={confirmDisabled || loading}
          style={destructive ? destructiveStyle : undefined}
        >
          {confirmText ?? 'Confirm'}
        </Button>
      )}
    </StyledFooter>
  );
};
